import { DatepickerRange, TableState, ValueAxisLabelsConfig, MultiselectItem } from '@ui/aos-lib';
import { DeviceHealthPresentation } from 'src/app/pages/equipment/devices/components/device-details/device-details-metrics/device-details-metrics.types';
import { SortDescriptorDir } from 'src/app/shared/components/listing-page/listing-page.types';
import { FilterModel } from 'src/app/shared/components/listing-page/services/listing-page-model/listing-page-model.types';
import { DateTimeInterval } from 'src/app/shared/types/datetime.types';
import { TableData } from 'src/app/shared/types/api-response.types';
import { createDynamicMinAndMaxValueAxisLabelsConfig } from 'src/app/shared/utils/chart.utils';
import { WidgetConfig } from '../../components/consumption-widget/consumption-widget.interface';
import { ConsumptionWidgetService } from '../../components/consumption-widget/consumption-widget.service';
import {
  ConsumptionReads,
  ConsumptionTableFieldName,
  ConsumptionWidgetResponseOverview
} from '../../shared/consumption.types';
import { TranslateRegistry } from 'src/app/services/translate/translate.registry';
import { ReportGroupFilterResponse } from '../../../../services/api/report-group-filter/report-group-filter-api.types';
import { initialRequestStatusState, RequestStatusState } from '@portal/shared/store/request-status';
import { GetTableDataItem } from 'src/app/pages/equipment/devices/components/device-details/device-details-metrics/meter/meter-consumption-tab-content/meter-consumption-tab-content.types';

export interface ConsumptionAnalyticsState {
  widget: WidgetConfig;
  widgetWithUOM: WidgetConfig;
  reportGroupFilter: Omit<FilterModel, 'filterType'>;
  dateRange: null | DatepickerRange;
  previousPeriod: string | null;
  interval: DateTimeInterval;
  widgetRequest: RequestStatusState;
  consumptionsRequest: RequestStatusState;
  consumptionReadsRequest: RequestStatusState;
  consumptions: DeviceHealthPresentation[];
  consumptionsWithUOM: DeviceHealthPresentation[];
  consumptionReads: TableData<ConsumptionReads | GetTableDataItem>;
  consumptionReadsWithUOM: TableData<ConsumptionReads | GetTableDataItem>;
  tableState: TableState;
  chartSettings: {
    valueAxisLabelsConfig: ValueAxisLabelsConfig;
  };
  isChartNumbersShown: boolean;
  filtersLoaded: boolean;
  reportGroups: ReportGroupFilterResponse[];
  commodity: string;
  unitOfMeasure: {
    model: Omit<FilterModel, 'filterType'> & { isDisabled: boolean; selectedItem: MultiselectItem };
    multiplier: number;
    reportGroupUID: string | null;
  };
  flowDirectionFilter: Pick<FilterModel, 'selected' | 'items' | 'width' | 'isMultiTag'>;
}

const widgetInitialState: ConsumptionWidgetResponseOverview = {
  currentPeriod: {
    averageValue: null,
    maxValue: null,
    minValue: null,
    totalValue: null,
    unitOfMeasure: null,
    multiplier: null
  },
  previousPeriod: null,
  totalValueChangeInPercent: null,
  maxValueChangeInPercent: null,
  averageValueChangeInPercent: null,
  minValueChangeInPercent: null
};

export const initialState: ConsumptionAnalyticsState = {
  widget: new ConsumptionWidgetService().getOverviewWidgetConfig(widgetInitialState),
  widgetWithUOM: new ConsumptionWidgetService().getOverviewWidgetConfig(widgetInitialState),
  reportGroupFilter: {
    title: TranslateRegistry.get.FILTER.TITLE.REPORT_GROUP,
    selected: [],
    items: [],
    width: 15,
    isMultiselect: false
  },
  filtersLoaded: false,
  dateRange: null,
  previousPeriod: null,
  interval: DateTimeInterval.Day,
  widgetRequest: initialRequestStatusState,
  consumptionsRequest: initialRequestStatusState,
  consumptionReadsRequest: initialRequestStatusState,
  consumptions: [],
  consumptionsWithUOM: [],
  consumptionReads: {
    data: [],
    total: 0
  },
  consumptionReadsWithUOM: {
    data: [],
    total: 0
  },
  tableState: {
    skip: 0,
    take: 25,
    sort: [
      {
        field: ConsumptionTableFieldName.Date,
        dir: SortDescriptorDir.desc
      }
    ]
  },
  chartSettings: {
    valueAxisLabelsConfig: createDynamicMinAndMaxValueAxisLabelsConfig([])
  },
  unitOfMeasure: {
    model: {
      title: TranslateRegistry.get.FILTER.COMMON.UNIT,
      selected: [],
      items: [],
      width: 106,
      isMultiselect: false,
      isDisabled: true,
      selectedItem: { text: TranslateRegistry.get.FILTER.COMMON.SELECT, value: 'Select' }
    },
    multiplier: 1,
    reportGroupUID: null
  },
  isChartNumbersShown: false,
  reportGroups: [],
  commodity: '',
  flowDirectionFilter: {
    selected: [],
    items: [],
    width: 168,
    isMultiTag: true
  }
};
